/**
 * OctreePointCloudViewer — Three.js (WebGL) renderer for massive point clouds
 * served via the backend octree LOD pipeline.
 *
 * Capacity target: 500M+ points. Memory peak is bounded by the LOD policy:
 *   - lod0 (1/64 stride) loads first across all visible cells for instant paint
 *   - lod1 / lod2 are upgraded per-cell based on (distance, screen-space size)
 *   - cells outside the view frustum are dropped from GPU memory
 *
 * Wire format per cell (matches /points-bin):
 *   uint32 count + float32 xyz × count + uint8 rgb × count
 *
 * Usage:
 *   <OctreePointCloudViewer projectId sessionId fileId height />
 *
 * The component lazy-loads three.js via the existing app bundle.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { authFetch } from '@/lib/api';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

const LOD_COLORS = ['#06b6d4', '#10b981', '#a3e635']; // debug overlay

export default function OctreePointCloudViewer({
  projectId, sessionId, fileId, height = 500,
  onCameraChange, // optional — { position, target } in world units
}) {
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const cellsRef = useRef(new Map());        // key → { lod, points: THREE.Points, geom }
  const manifestRef = useRef(null);
  const fetchInFlightRef = useRef(new Set()); // `${key}:lod${n}` so we don't double-fetch
  const [manifest, setManifest] = useState(null);
  const [loaded, setLoaded] = useState({ cells: 0, points: 0 });
  const [error, setError] = useState(null);
  const [debug, setDebug] = useState(false);

  const baseUrl = useMemo(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    return `${base}/api/v1/projects/${projectId}/scan3d/sessions/${sessionId}/files/${fileId}/octree`;
  }, [projectId, sessionId, fileId]);

  // ── 1. Mount Three.js scene ─────────────────────────────────────────────
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;
    const w = mount.clientWidth || 800;
    const h = height;
    const renderer = new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    renderer.setClearColor(0x06090f, 1);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(50, w / h, 0.1, 1e7);
    camera.position.set(0, 0, 50);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;

    // Soft grid + axes for orientation
    const grid = new THREE.GridHelper(100, 20, 0x1f2937, 0x111827);
    grid.rotation.x = Math.PI / 2;
    scene.add(grid);
    scene.add(new THREE.AxesHelper(5));

    sceneRef.current = scene;
    rendererRef.current = renderer;
    cameraRef.current = camera;
    controlsRef.current = controls;

    let raf;
    const animate = () => {
      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    const onResize = () => {
      const W = mount.clientWidth || w;
      camera.aspect = W / h;
      camera.updateProjectionMatrix();
      renderer.setSize(W, h);
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    if (onCameraChange) {
      controls.addEventListener('change', () => onCameraChange({
        position: camera.position.clone(),
        target: controls.target.clone(),
      }));
    }

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  // ── 2. Load manifest ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setManifest(null);
    authFetch(`${baseUrl}/manifest`)
      .then(r => {
        if (!r.ok) throw new Error(`Octree manifest ${r.status}`);
        return r.json();
      })
      .then(m => {
        if (cancelled) return;
        manifestRef.current = m;
        setManifest(m);
        // Frame the camera on the global bbox
        const cam = cameraRef.current; const controls = controlsRef.current;
        if (cam && controls) {
          const bb = m.bbox;
          const ext = Math.max(bb.max.x - bb.min.x, bb.max.y - bb.min.y, bb.max.z - bb.min.z);
          cam.position.set(ext * 1.2, ext * 0.9, ext * 1.4);
          controls.target.set(0, 0, 0);
          cam.far = Math.max(ext * 10, 1e5);
          cam.updateProjectionMatrix();
          controls.update();
        }
      })
      .catch(e => {
        if (!cancelled) setError(e.message);
      });
    return () => { cancelled = true; };
  }, [baseUrl]);

  // ── 3. LOD streamer ─────────────────────────────────────────────────────
  // Re-evaluate which cell × LOD pairs we want based on the current camera.
  useEffect(() => {
    if (!manifest) return undefined;
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    const scene = sceneRef.current;
    if (!camera || !controls || !scene) return undefined;

    let stop = false;

    const evaluate = () => {
      const cells = manifest.cells || [];
      if (!cells.length) return;
      // Pre-compute frustum for culling
      camera.updateMatrixWorld();
      const projScreenMatrix = new THREE.Matrix4()
        .multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
      const frustum = new THREE.Frustum().setFromProjectionMatrix(projScreenMatrix);

      const camPos = camera.position;
      // Pick LOD based on distance from camera to cell centroid relative to cell size
      const cellExt = Math.max(manifest.cellSize.x, manifest.cellSize.y, manifest.cellSize.z);
      const pickLod = (cellBBox) => {
        const cx = (cellBBox.min.x + cellBBox.max.x) / 2;
        const cy = (cellBBox.min.y + cellBBox.max.y) / 2;
        const cz = (cellBBox.min.z + cellBBox.max.z) / 2;
        const dx = camPos.x - cx, dy = camPos.y - cy, dz = camPos.z - cz;
        const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
        // Empirical: <2× cell-extent → LOD2, <8× → LOD1, else LOD0
        if (d < cellExt * 2) return 2;
        if (d < cellExt * 8) return 1;
        return 0;
      };

      const wanted = [];
      for (const c of cells) {
        const box = new THREE.Box3(
          new THREE.Vector3(c.bbox.min.x, c.bbox.min.y, c.bbox.min.z),
          new THREE.Vector3(c.bbox.max.x, c.bbox.max.y, c.bbox.max.z)
        );
        if (!frustum.intersectsBox(box)) continue;
        const lod = pickLod(c.bbox);
        wanted.push({ key: c.key, lod, cell: c, distance:
          camPos.distanceTo(box.getCenter(new THREE.Vector3())) });
      }
      // Sort: closer first, lower lods (1/64) finish first for fast paint
      wanted.sort((a, b) => a.distance - b.distance);

      // Drop cells that fell out of view → free GPU memory
      for (const [key, holder] of cellsRef.current.entries()) {
        if (!wanted.find(w => w.key === key)) {
          scene.remove(holder.points);
          holder.points.geometry.dispose();
          holder.points.material.dispose();
          cellsRef.current.delete(key);
        }
      }

      // Schedule fetches (cap parallel concurrency to keep network sane)
      const MAX_PARALLEL = 6;
      let inFlight = fetchInFlightRef.current.size;
      const fetchOne = (w) => {
        const tag = `${w.key}:lod${w.lod}`;
        if (fetchInFlightRef.current.has(tag)) return;
        const existing = cellsRef.current.get(w.key);
        if (existing && existing.lod >= w.lod) return; // already at this LOD or better
        fetchInFlightRef.current.add(tag);
        inFlight += 1;
        authFetch(`${baseUrl}/cell/${w.key}?lod=${w.lod}`)
          .then(r => { if (!r.ok) throw new Error(`cell ${w.key} lod${w.lod} ${r.status}`); return r.arrayBuffer(); })
          .then(buf => {
            if (stop) return;
            const view = new DataView(buf);
            const count = view.getUint32(0, true);
            if (!count) return;
            const positions = new Float32Array(buf, 4, count * 3);
            const colorsU8 = new Uint8Array(buf, 4 + count * 12, count * 3);
            // Convert RGB byte → float [0,1] for THREE.Color
            const colors = new Float32Array(count * 3);
            for (let i = 0; i < count * 3; i++) colors[i] = colorsU8[i] / 255;

            const geom = new THREE.BufferGeometry();
            geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
            geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
            const mat = new THREE.PointsMaterial({
              size: w.lod === 2 ? 0.05 : w.lod === 1 ? 0.08 : 0.15,
              vertexColors: true,
              sizeAttenuation: true,
            });
            const pts = new THREE.Points(geom, mat);

            const prior = cellsRef.current.get(w.key);
            if (prior) {
              scene.remove(prior.points);
              prior.points.geometry.dispose();
              prior.points.material.dispose();
            }
            scene.add(pts);
            cellsRef.current.set(w.key, { lod: w.lod, points: pts, geom });

            const stats = { cells: cellsRef.current.size, points: 0 };
            cellsRef.current.forEach((h) => { stats.points += h.points.geometry.attributes.position.count; });
            setLoaded(stats);
          })
          .catch(() => { /* swallow — next eval will retry */ })
          .finally(() => {
            fetchInFlightRef.current.delete(tag);
          });
      };

      for (const w of wanted) {
        if (inFlight >= MAX_PARALLEL) break;
        fetchOne(w);
      }
    };

    // Debounce camera-driven re-evaluations
    let timer = null;
    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(evaluate, 120);
    };
    controls.addEventListener('change', schedule);
    // Initial pass
    schedule();

    return () => {
      stop = true;
      clearTimeout(timer);
      controls.removeEventListener('change', schedule);
    };
  }, [manifest, baseUrl]);

  return (
    <div ref={mountRef} className="relative" style={{ height, width: '100%' }} data-testid="octree-pcv">
      <div className="absolute top-2 left-2 flex flex-wrap items-center gap-1.5">
        {!manifest && !error && (
          <span className="text-[10px] px-2 py-0.5 rounded bg-black/60 text-cyan-400 animate-pulse">
            Loading octree manifest…
          </span>
        )}
        {error && (
          <span className="text-[10px] px-2 py-0.5 rounded bg-red-900/60 text-red-300" data-testid="octree-error">
            {error}
          </span>
        )}
        {manifest && (
          <>
            <span className="text-[10px] px-2 py-0.5 rounded bg-black/60 text-white" data-testid="octree-stats">
              {loaded.cells}/{(manifest.cells || []).length} cells · {(loaded.points / 1e6).toFixed(2)}M loaded · {(manifest.totalPoints / 1e6).toFixed(1)}M total
            </span>
            <button onClick={() => setDebug(d => !d)} className="text-[9px] px-1.5 py-0.5 rounded bg-black/40 text-slate-400">
              {debug ? 'debug ON' : 'debug'}
            </button>
          </>
        )}
      </div>
      {debug && manifest && (
        <div className="absolute bottom-2 left-2 text-[9px] font-mono opacity-70 text-slate-300 max-w-[40%]">
          {Array.from(cellsRef.current.entries()).slice(0, 12).map(([k, h]) => (
            <div key={k}>
              <span style={{ color: LOD_COLORS[h.lod] || '#fff' }}>lod{h.lod}</span> {k} · {h.points.geometry.attributes.position.count.toLocaleString()} pts
            </div>
          ))}
        </div>
      )}
      <div className="absolute bottom-2 right-2 text-[8px] px-2 py-0.5 rounded bg-black/40 text-slate-400">
        Drag to orbit · Scroll to zoom · Right-drag to pan
      </div>
    </div>
  );
}
